import React, { useEffect, useRef, useState } from 'react'
import styles from './index.module.scss'
import Label from "../../../../../../components/Label";
import { dispatchProjectNotification } from '../../../../../../utils/projectNotification'
import UsersAvatar from "../../../../../../components/UsersAvatar";
import TaskModal from "../../../../../../components/Modal/components/TaskModal";
import TaskService from "../../../../../../services/task.service";
import { Project } from "../../../../../../types";
import EditIcon from "../../../../../../assets/icon/EditIcon";
import DotsVerticalIcon from "../../../../../../assets/icon/DotsVerticalIcon";
import DuplicateIcon from "../../../../../../assets/icon/DuplicateIcon";
import CopyLinkIcon from "../../../../../../assets/icon/CopyLinkIcon";
import TaskMoveIcon from "../../../../../../assets/icon/TaskMoveIcon";
import ArchiveIcon from "../../../../../../assets/icon/ArchiveIcon";
import TrashIcon from "../../../../../../assets/icon/TrashIcon";
import ActionCheckIcon from "../../../../../../assets/icon/ActionCheckIcon";
import CloseIcon from "../../../../../../assets/icon/CloseIcon";
import DropDown from "../../../../../../components/DropDown";
import ProjectDeleteModal from "../../../../../../components/ProjectControls/ProjectDeleteModal";
import TaskMoveModal from "../../../../../../components/ProjectControls/TaskMoveModal";
import { TaskDropTarget } from '../../../../hooks/useTaskDragAndDrop';
import { buildTaskUpdatePayload, updateTaskChangedFields } from '../../../../utils/taskPayload';
import { stripTaskTimeTrackingFields } from '../../../../utils/taskPayload';
import { getTaskUsers } from '../../../../utils/taskUsers';
import { getTaskPriority, getTaskPriorityBackground } from '../../../../../../utils/taskPriority';
import { formatTaskDateTime } from '../../../../../../utils/formatDate';
import StarsIcon from '../../../../../../assets/icon/StarsIcon';
import { moveTaskToProject } from '../../../../utils/taskMove';
import { copyProjectTaskLink } from '../../../../../../utils/projectLink';
import { persistRelatedTask } from '../../../../../../utils/persistRelatedTask';
import { getTaskType } from '../../../../../../components/Modal/components/TaskModal/utils/taskTypes';

interface Props {
    task: any
    parentTitle?: string
    getTasks: () => void
    onTaskUpdated?: (task: any | null, options?: { remove?: boolean }) => void
    dragPreviewPosition?: TaskDropTarget['position'] | null
    draggable?: boolean
    isDragging?: boolean
    isPending?: boolean
    onDragEnd?: () => void
    onDragStart?: (taskId: number) => void
    onDrop?: (target?: TaskDropTarget | null) => Promise<void> | void
    onHover?: (target: TaskDropTarget) => void
    project: Project
    projectUsers?: any[]
}

export default function ProjectItem({
    task,
    parentTitle,
    project,
    projectUsers = [],
    getTasks,
    onTaskUpdated,
    dragPreviewPosition = null,
    draggable = true,
    isDragging = false,
    isPending = false,
    onDragEnd,
    onDragStart,
    onDrop,
    onHover,
}: Props) {
    const [isShowTask, setIsShowTask] = useState(false)
    const [isRenaming, setIsRenaming] = useState(false)
    const [renameTitle, setRenameTitle] = useState(task.title || '')
    const [isDeleteOpen, setIsDeleteOpen] = useState(false)
    const [isMoveOpen, setIsMoveOpen] = useState(false)
    const renameInputRef = useRef<HTMLInputElement>(null)
    const isArchived = Boolean(task.is_archived || task.archived_at)
    const taskUsers = getTaskUsers(task, projectUsers)
    const taskPriority = getTaskPriority(task.priority)
    const taskType = getTaskType(task.task_type)

    useEffect(() => {
        setRenameTitle(task.title || '')
    }, [task.title])

    useEffect(() => {
        if (isRenaming) {
            setTimeout(() => renameInputRef.current?.focus(), 50)
        }
    }, [isRenaming])

    const updateTask = async (taskData: any) => {
        await TaskService.update(task.id, stripTaskTimeTrackingFields(taskData))

        await Promise.all((taskData.deletedRelatedTaskIds || []).map((relatedTaskId: number | string) => (
            TaskService.delete(Number(relatedTaskId))
        )))

        await Promise.all((taskData.relatedTasks || []).filter(Boolean).map((currentTask: any) => (
            persistRelatedTask({
                task: currentTask,
                parentTaskId: taskData.id,
                projectId: project.id,
            })
        )))

        setIsShowTask(false)
        await getTasks()
    }

    const startRename = () => {
        setRenameTitle(task.title || '')
        setIsRenaming(true)
    }

    const confirmRename = async () => {
        if (!renameTitle.trim()) return

        try {
            const updatedTask = await updateTaskChangedFields(task.id, project.id, {
                title: renameTitle.trim(),
            })
            onTaskUpdated?.(updatedTask)
            setIsRenaming(false)
            await getTasks()
        } catch (error) {
            console.error('Failed to rename task:', error)
        }
    }

    const duplicateTask = async () => {
        try {
            const duplicatedTask = await TaskService.create(buildTaskUpdatePayload(task, project.id, {
                title: `${task.title || 'Задача'} (копия)`,
            }))

            const relatedTasks = Array.isArray(task.relatedTasks)
                ? task.relatedTasks.filter((currentTask: any) => !!currentTask)
                : []

            await Promise.all(relatedTasks.map((relatedTask: any) => TaskService.create(buildTaskUpdatePayload(relatedTask, project.id, {
                parent_id: duplicatedTask.id,
            }))))

            await getTasks()

            dispatchProjectNotification({
                text: 'Задача успешно продублирована',
                action: {
                    label: 'Отменить',
                    onClick: () => {
                        void (async () => {
                            try {
                                await TaskService.delete(duplicatedTask.id)
                                await getTasks()
                            } catch (error) {
                                console.error('Failed to undo task duplication:', error)
                            }
                        })()
                    },
                },
            })
        } catch (error) {
            console.error('Failed to duplicate task:', error)
        }
    }

    const copyTaskLink = async () => {
        try {
            await copyProjectTaskLink(project.id, task.id, 'board')
            dispatchProjectNotification({
                text: 'Ссылка скопирована',
            })
        } catch (error) {
            console.error('Failed to copy task link:', error)
        }
    }

    const moveTask = async (targetProject: Project) => {
        try {
            await moveTaskToProject(task, project.id, targetProject)
            onTaskUpdated?.(task, { remove: true })
            setIsMoveOpen(false)
            await getTasks()

            dispatchProjectNotification({
                text: 'Задача перемещена в другой проект',
            })
        } catch (error) {
            console.error('Failed to move task:', error)
        }
    }

    const deleteTask = async () => {
        try {
            await TaskService.delete(task.id)
            onTaskUpdated?.(task, { remove: true })
            setIsDeleteOpen(false)
            await getTasks()
        } catch (error) {
            console.error('Failed to delete task:', error)
        }
    }

    const archiveTask = async () => {
        try {
            const wasArchived = isArchived
            const updatedTask = wasArchived
                ? await TaskService.unarchive(task.id)
                : await TaskService.archive(task.id)

            onTaskUpdated?.(updatedTask)
            await getTasks()

            if (!wasArchived) {
                dispatchProjectNotification({
                    text: 'Задача перенесена в архив',
                    action: {
                        label: 'Отменить',
                        onClick: () => {
                            void (async () => {
                                try {
                                    const restored = await TaskService.unarchive(task.id)
                                    onTaskUpdated?.(restored)
                                    getTasks()
                                } catch (error) {
                                    console.error('Failed to undo task archive:', error)
                                }
                            })()
                        },
                    },
                })
            }
        } catch (error) {
            console.error('Failed to change task archive state:', error)
        }
    }

    return (
        <div>
            <div
                className={`${styles.projectItem} ${isDragging ? styles.projectItemDragging : ''} ${isPending ? styles.projectItemPending : ''} ${dragPreviewPosition === 'before' ? styles.projectItemDropBefore : ''} ${dragPreviewPosition === 'after' ? styles.projectItemDropAfter : ''}`}
                draggable={draggable && !isRenaming}
                onClick={() => !isRenaming && !isDragging && setIsShowTask(task)}
                onDragEnd={onDragEnd}
                onDragOver={(event) => {
                    if (!onHover) {
                        return
                    }

                    event.preventDefault()
                    event.stopPropagation()

                    const rect = event.currentTarget.getBoundingClientRect()
                    const isAfter = event.clientY - rect.top > rect.height / 2

                    onHover({
                        mode: 'task',
                        position: isAfter ? 'after' : 'before',
                        status: task.status,
                        taskId: task.id,
                    })
                }}
                onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move'
                    event.dataTransfer.setData('text/plain', String(task.id))
                    onDragStart?.(task.id)
                }}
                onDrop={(event) => {
                    if (!onDrop) {
                        return
                    }

                    event.preventDefault()
                    event.stopPropagation()

                    const rect = event.currentTarget.getBoundingClientRect()
                    const isAfter = event.clientY - rect.top > rect.height / 2

                    void onDrop({
                        mode: 'task',
                        position: isAfter ? 'after' : 'before',
                        status: task.status,
                        taskId: task.id,
                    })
                }}
            >
                <div className={styles.projectItem__top}>
                    {isRenaming ? (
                        <div className={styles.projectItem__renameWrap} onClick={(event) => event.stopPropagation()}>
                            <input
                                ref={renameInputRef}
                                className={styles.projectItem__renameInput}
                                value={renameTitle}
                                onChange={(e) => setRenameTitle(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') confirmRename()
                                    if (e.key === 'Escape') setIsRenaming(false)
                                }}
                            />
                            <div className={styles.projectItem__renameConfirm} onClick={confirmRename}>
                                <ActionCheckIcon />
                            </div>
                            <div className={styles.projectItem__renameCancel} onClick={() => setIsRenaming(false)}>
                                <CloseIcon />
                            </div>
                        </div>
                    ) : (
                        <div className={styles.projectItem__titleGroup}>
                            <div className={styles.projectItem__titleRow}>
                                <span className={styles.projectItem__typeIcon} aria-hidden="true">
                                    {taskType.icon}
                                </span>
                                <div className={styles.projectItem__title}>
                                    {task.title}
                                </div>
                            </div>
                            {task.parent_id && (
                                <span className={styles.projectItem__parent} title={parentTitle || String(task.parent_id)}>
                                    ↳ {parentTitle || 'Этап'}
                                </span>
                            )}
                        </div>
                    )}
                    <div className={styles.projectItem__menu} onClick={(event) => event.stopPropagation()}>
                        <DropDown
                            closeDropDownByPick
                            position="right"
                            btn={(
                                <div className={styles.projectItem__menuBtn}>
                                    <DotsVerticalIcon />
                                </div>
                            )}
                        >
                            <div className={styles.projectItem__dropdown}>
                                <div className={styles.projectItem__dropdownItem} onClick={startRename}>
                                    <EditIcon />
                                    <span>Переименовать</span>
                                </div>
                                <div className={styles.projectItem__dropdownItem} onClick={duplicateTask}>
                                    <DuplicateIcon />
                                    <span>Дублировать</span>
                                </div>
                                <div className={styles.projectItem__dropdownItem} onClick={copyTaskLink}>
                                    <span className={styles.projectItem__dropdownIconLarge}>
                                        <CopyLinkIcon />
                                    </span>
                                    <span>Копировать ссылку</span>
                                </div>
                                <div className={styles.projectItem__dropdownItem} onClick={() => setIsMoveOpen(true)}>
                                    <span className={styles.projectItem__dropdownMoveIcon}>
                                        <TaskMoveIcon />
                                    </span>
                                    <span>Переместить</span>
                                </div>
                                <div className={styles.projectItem__dropdownItem} onClick={archiveTask}>
                                    <ArchiveIcon />
                                    <span>{isArchived ? 'Вернуть с архива' : 'Архивировать'}</span>
                                </div>
                                <div className={`${styles.projectItem__dropdownItem} ${styles.projectItem__dropdownItemDanger}`} onClick={() => setIsDeleteOpen(true)}>
                                    <TrashIcon />
                                    <span>Удалить</span>
                                </div>
                            </div>
                        </DropDown>
                    </div>
                </div>
                <div className={styles.projectItem__info}>
                    <Label title={formatTaskDateTime(task.created_at)} backgroundColor={"#F6F5F9"} iconUrl={'/icon/calendar.svg'} />
                    {taskPriority && (
                        <Label
                            title={taskPriority.title}
                            backgroundColor={getTaskPriorityBackground(task.priority)}
                            icon={<StarsIcon color={taskPriority.color}/>}
                        />
                    )}
                </div>
                <div className={styles.projectItem__info}>
                    <UsersAvatar users={taskUsers} />
                    <div className={styles.projectItem__info_actions}>
                        <div className={styles.projectItem__info_actions_item}>
                            <img src="/icon/attach.svg" alt="attach" />
                            {task?.attachments_count || 0}
                        </div>
                        <div className={styles.projectItem__info_actions_item}>
                            <img src="/icon/chat.svg" alt="chat" />
                            {task?.comments_count || 0}
                        </div>
                        <div className={styles.projectItem__info_actions_item}>
                            <img src="/icon/filled.svg" alt="filled" />
                            {task?.subtasks_count || 0}
                        </div>
                    </div>
                </div>
            </div>
            <ProjectDeleteModal
                isOpen={isDeleteOpen}
                title="Удалить задачу?"
                text={`Вы уверены, что хотите удалить задачу «${task.title || 'Без названия'}»? Это действие нельзя отменить.`}
                onClose={() => setIsDeleteOpen(false)}
                onConfirm={deleteTask}
            />
            <TaskMoveModal
                currentProjectId={project.id}
                isOpen={isMoveOpen}
                onClose={() => setIsMoveOpen(false)}
                onConfirm={moveTask}
            />
            {isShowTask && (
                <TaskModal
                    isOpen={isShowTask}
                    onClose={() => setIsShowTask(false)}
                    task={isShowTask}
                    project={project}
                    createTask={updateTask}
                    onEditCommitted={getTasks}
                />

            )}
        </div>
    )
}
